import { useState, useCallback, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Container,
  Typography,
  Card,
  Box,
  Grid,
  Chip,
  IconButton,
  Tooltip,
  Paper,
  Divider,
  Link,
  Stack,
  Button,
  Snackbar,
  Pagination,
  CircularProgress,
} from '@mui/material';
import { useQuery, useLazyQuery } from '@apollo/client';
import { alpha, useTheme } from '@mui/material/styles';
import Iconify from 'src/components/iconify';
import Label from 'src/components/label';
import { SEARCH_TAGS_AND_HEADERS, GET_TECHNOLOGY_TAGS_WITH_COUNT } from 'src/graphql/tag-search-queries';
import LoadingBar from 'src/components/loading-bar';
import SearchSection from 'src/components/search/search-section';
import { getProviderIcon, getProviderTypeColor } from 'src/utils/provider-icons';

// Move PlaceholderCard outside of TechIconsDisplay
const PlaceholderCard = ({ theme }) => (
  <Card 
    sx={{ 
      p: 3, 
      mb: 3, 
      borderRadius: 2,
      boxShadow: theme.customShadows.card,
      bgcolor: 'background.paper',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: 200
    }}
  >
    <Iconify 
      icon="mdi:code-tags" 
      width={60} 
      height={60} 
      sx={{ 
        color: alpha(theme.palette.grey[500], 0.6),
        mb: 2
      }} 
    />
    <Typography variant="subtitle1" sx={{ color: 'text.secondary', textAlign: 'center' }}>
      No technology icons to display
    </Typography>
    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 1 }}>
      Try a different search or check back later.
    </Typography>
  </Card>
);

PlaceholderCard.propTypes = {
  theme: PropTypes.object.isRequired,
};

// Move IconPlaceholder outside of the other components
const IconPlaceholder = ({ safeColor, safeName }) => (
  <Box
    sx={{
      width: 44,
      height: 44,
      borderRadius: '8px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      bgcolor: alpha(safeColor, 0.2),
      fontSize: '1rem',
      fontWeight: 'bold',
      color: safeColor
    }}
  >
    {safeName.slice(0, 2).toUpperCase()}
  </Box>
);

IconPlaceholder.propTypes = {
  safeColor: PropTypes.string.isRequired,
  safeName: PropTypes.string.isRequired,
};

// Move IconComponent outside of TechIconsDisplay
const TechIcon = ({ icon, iconFallbacks, safeColor, safeName }) => {
  // If iconFallbacks is available, try each icon in sequence
  if (iconFallbacks && iconFallbacks.length > 0) {
    return (
      <Iconify
        icon={icon}
        width={44}
        height={44}
        sx={{ 
          color: safeColor,
          filter: 'drop-shadow(0 2px 2px rgba(0,0,0,0.1))'
        }}
        fallback={iconFallbacks}
        onError={() => (
          <IconPlaceholder safeColor={safeColor} safeName={safeName} />
        )}
      />
    );
  }
  
  // Try to use the icon, with a fallback to a placeholder
  return (
    <Iconify
      icon={icon || 'mdi:code-tags'}
      width={44}
      height={44}
      sx={{ 
        color: safeColor,
        filter: 'drop-shadow(0 2px 2px rgba(0,0,0,0.1))'
      }}
      fallback={<IconPlaceholder safeColor={safeColor} safeName={safeName} />}
    />
  );
};

TechIcon.propTypes = {
  icon: PropTypes.string,
  iconFallbacks: PropTypes.arrayOf(PropTypes.string),
  safeColor: PropTypes.string.isRequired,
  safeName: PropTypes.string.isRequired,
};

// Tech Icons Display Component that uses real data from GraphQL search results
function TechIconsDisplay({ onTechClick }) {
  const theme = useTheme();
  const [common, setCommon] = useState([]);
  const [rare, setRare] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);
  
  // Use lazy query instead of auto-executing query
  const [fetchTechData, { called }] = useLazyQuery(GET_TECHNOLOGY_TAGS_WITH_COUNT, {
    fetchPolicy: 'no-cache',
    onCompleted: (data) => {
      if (data?.domain_tag_association) {
        processTechnologyData(data.domain_tag_association);
        setLoading(false);
        setInitialDataLoaded(true);
      }
    },
    onError: (err) => {
      console.error('Error fetching technology data:', err);
      setError(err);
      setLoading(false);
    }
  });
  
  // Execute query once on component mount
  useEffect(() => {
    if (!called && !initialDataLoaded) {
      setLoading(true);
      fetchTechData({
        variables: {
          since: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString()
        }
      });
    }
  }, [called, fetchTechData, initialDataLoaded]);
  
  // Process technology data outside of the component render cycle
  function processTechnologyData(associations) {
    console.log('Processing tech data with associations:', associations.length);
    
    try {
      // Create map to count unique domains per tag
      const tagMap = new Map();
      
      // Process each association
      associations.forEach(assoc => {
        if (!assoc?.domain_tag?.tag || !assoc?.live_domain?.domain) return;
        
        const tag = assoc.domain_tag.tag.trim();
        if (!tag) return;
        
        if (!tagMap.has(tag)) {
          tagMap.set(tag, {
            name: tag,
            count: 0,
            domains: new Set()
          });
        }
        
        const tagData = tagMap.get(tag);
        tagData.domains.add(assoc.live_domain.domain);
        tagData.count = tagData.domains.size;
      });
      
      // Convert to array and enrich with icons/colors
      const tagsArray = Array.from(tagMap.values())
        .filter(tag => tag.count > 0)
        .map(tag => {
          const { icon, color, category } = getTechIconInfo(tag.name);
          return {
            ...tag,
            icon,
            color,
            category,
            domains: Array.from(tag.domains)
          };
        });
      
      // Split into most common and least common
      const mostCommon = [...tagsArray]
        .sort((a, b) => b.count - a.count)
        .slice(0, 30);
        
      const leastCommon = [...tagsArray]
        .sort((a, b) => a.count - b.count)
        .slice(0, 10);
        
      // Update state
      setCommon(mostCommon);
      setRare(leastCommon);
      
      console.log('Processed tech data:', { common: mostCommon.length, rare: leastCommon.length });
    } catch (e) {
      console.error('Error in processing tech data:', e);
    }
  }

  // Show loading state
  if (loading && !initialDataLoaded) {
    return (
      <Card sx={{ p: 3, mb: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
        <CircularProgress />
        <Typography variant="body2" sx={{ ml: 2 }}>
          Loading technology data...
        </Typography>
      </Card>
    );
  }

  // Show error state
  if (error && !initialDataLoaded) {
    return (
      <Card sx={{ p: 3, mb: 3, bgcolor: alpha(theme.palette.error.main, 0.1) }}>
        <Typography variant="subtitle1" color="error" sx={{ mb: 1 }}>
          Error loading technology data
        </Typography>
        <Typography variant="body2">
          {error.message || 'Please try again later'}
        </Typography>
      </Card>
    );
  }
  
  // Show empty state
  if (initialDataLoaded && rare.length === 0 && common.length === 0) {
    return (
      <Card sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>
          No technologies found
        </Typography>
        <Typography variant="body2" color="text.secondary">
          No unique technologies have been detected in the last 7 days.
        </Typography>
      </Card>
    );
  }

  // Category labels with nice formatting
  const categoryLabels = {
    cloud: 'Cloud Providers',
    cdn: 'CDN Services',
    security: 'Security & Protection',
    frontend: 'Frontend Frameworks',
    backend: 'Backend Technologies',
    database: 'Databases',
    devops: 'DevOps Tools',
    devtools: 'Development Tools',
    cms: 'Content Management',
    payment: 'Payment Solutions',
    communication: 'Communication Tools',
    media: 'Media Services',
    analytics: 'Analytics Tools',
    other: 'Other Technologies'
  };
  
  // Category colors for visual separation
  const categoryColors = {
    cloud: theme.palette.primary.main,
    cdn: theme.palette.info.main,
    security: theme.palette.warning.main,
    frontend: theme.palette.success.main,
    backend: theme.palette.secondary.main,
    database: theme.palette.error.main,
    devops: '#9c27b0', // purple
    devtools: '#795548', // brown
    cms: '#009688', // teal
    payment: '#673ab7', // deep purple
    communication: '#3f51b5', // indigo
    media: '#607d8b', // blue grey
    analytics: '#ff5722', // deep orange
    other: '#9e9e9e' // grey
  };
  
  // Group technologies by category
  const groupByCategory = (techs) => techs.reduce((acc, tech) => {
      // Use 'other' as fallback if category is missing
      const category = tech.category || 'other';
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(tech);
      return acc;
    }, {});
  
  const commonTechByCategory = groupByCategory(common);
  
  // Render a tech icon (used for both common tech and recent discoveries)
  const renderTechIcon = (tech, showCount = false, index = 0) => {
    // Safety check - ensure tech has required properties with defaults
    const safeColor = tech.color || theme.palette.grey[500];
    const safeName = tech.name || 'Unknown';
    const safeCount = tech.count || 0;
    
    return (
      <Tooltip
        key={safeName}
        title={`${safeName} (found on ${safeCount} domain${safeCount !== 1 ? 's' : ''})`}
        arrow
      >
        <Paper
          sx={{
            p: 1.5,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderRadius: 2,
            width: 90,
            height: showCount ? 130 : 90, // Increased height for rare tech items with count
            transition: 'all 0.3s ease',
            backgroundColor: alpha(safeColor, 0.1),
            border: `1px solid ${alpha(safeColor, 0.2)}`,
            cursor: 'pointer',
            opacity: 0,
            animation: `fadeIn 0.5s ease-out forwards ${index * 0.05}s`,
            '@keyframes fadeIn': {
              '0%': {
                opacity: 0,
                transform: 'translateY(10px)'
              },
              '100%': {
                opacity: 1,
                transform: 'translateY(0)'
              }
            },
            '&:hover': {
              transform: 'translateY(-5px)',
              boxShadow: `0 8px 16px 0 ${alpha(safeColor, 0.2)}`,
              backgroundColor: alpha(safeColor, 0.15),
            },
          }}
          onClick={() => onTechClick(safeName)}
        >
          <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', py: 1 }}>
            <TechIcon 
              icon={tech.icon} 
              iconFallbacks={tech.iconFallbacks} 
              safeColor={safeColor} 
              safeName={safeName} 
            />
          </Box>
          <Box sx={{ width: '100%' }}>
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                fontWeight: 'bold',
                color: 'text.primary',
                textAlign: 'center',
                lineHeight: 1.2,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                width: '100%',
                mb: showCount ? 1 : 0, // Add margin bottom when showing count
                fontSize: '0.7rem', // Slightly larger text
              }}
            >
              {safeName}
            </Typography>
            
            {showCount && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 0.5, mb: 0.5 }}>
                <Chip
                  label={`${safeCount}`}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: '0.65rem',
                    backgroundColor: alpha(safeColor, 0.1),
                    color: safeColor,
                    '& .MuiChip-label': {
                      px: 0.8,
                      py: 0
                    }
                  }}
                />
              </Box>
            )}
          </Box>
        </Paper>
      </Tooltip>
    );
  };
  
  return (
    <>
      {/* Rare Technologies Section */}
      {rare.length > 0 && (
        <Card 
          sx={{ 
            p: 3, 
            mb: 3, 
            borderRadius: 2, 
            boxShadow: theme.customShadows.card,
            bgcolor: 'background.paper',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" sx={{ color: theme.palette.text.primary }}>
              Rare Technologies (Last 7 Days)
            </Typography>
            <Tooltip title="Technologies found on the fewest number of domains in the last week">
              <IconButton size="small">
                <Iconify icon="mdi:information-outline" />
              </IconButton>
            </Tooltip>
          </Box>
          
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'flex-start' }}>
            {rare.map((tech, index) => renderTechIcon(tech, true, index))}
          </Box>
        </Card>
      )}

      {/* Common Tech Categories */}
      {common.length > 0 && (
        <Card sx={{ p: 3, mb: 3, borderRadius: 2, boxShadow: theme.customShadows.card }}>
          <Typography variant="h6" sx={{ mb: 3 }}>
            Popular Technologies (Last 7 Days)
          </Typography>
          
          {Object.keys(commonTechByCategory).map((category) => {
            // Get a safe color for the category - fallback to grey if not found
            const categoryColor = categoryColors[category] || theme.palette.grey[500];
            
            return (
            <Box key={category} sx={{ mb: 4 }}>
              <Box 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  mb: 2,
                  pb: 1,
                  borderBottom: `1px solid ${alpha(categoryColor, 0.2)}`
                }}
              >
                <Box 
                  sx={{ 
                    width: 12, 
                    height: 12, 
                    borderRadius: '50%', 
                    bgcolor: categoryColor,
                    mr: 1.5
                  }} 
                />
                <Typography 
                  variant="subtitle1" 
                  sx={{ 
                    fontWeight: 'bold',
                    color: alpha(categoryColor, 0.8)
                  }}
                >
                  {categoryLabels[category] || category}
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                {commonTechByCategory[category].map((tech, index) => renderTechIcon(tech, false, index))}
              </Box>
            </Box>
            );
          })}
        </Card>
      )}
      
      {/* Update PlaceholderCard usage to pass theme */}
      {initialDataLoaded && rare.length === 0 && common.length === 0 && <PlaceholderCard theme={theme} />}
    </>
  );
}

// Add PropTypes validation
TechIconsDisplay.propTypes = {
  onTechClick: PropTypes.func.isRequired,
};

// Helper function to auto-cast technologies to appropriate icons
const getTechIconInfo = (techName) => {
  if (!techName) {
    // Default fallback for empty or null input
    return {
      icon: 'mdi:code-tags',
      color: '#9e9e9e',
      category: 'other'
    };
  }

  // Ensure input is a string and lowercase for consistent matching
  const inputTag = (techName || '').toString().toLowerCase().trim();
  
  // Extract base technology name (remove anything after colon)
  const baseTechName = inputTag.split(':')[0].trim();
  
  // Technology mapping with exact matches for common technologies
  const techMappings = {
    // Cloud providers
    'aws': { icon: 'logos:aws', color: '#FF9900', category: 'cloud' },
    'amazon': { icon: 'logos:aws', color: '#FF9900', category: 'cloud' },
    'azure': { icon: 'logos:microsoft-azure', color: '#008AD7', category: 'cloud' },
    'microsoft': { icon: 'logos:microsoft-azure', color: '#008AD7', category: 'cloud' },
    'google': { icon: 'logos:google-cloud', color: '#4285F4', category: 'cloud' },
    'gcp': { icon: 'logos:google-cloud', color: '#4285F4', category: 'cloud' },
    'oracle': { icon: 'logos:oracle', color: '#C74634', category: 'cloud' },
    'digitalocean': { icon: 'logos:digitalocean', color: '#0080FF', category: 'cloud' },
    'heroku': { icon: 'logos:heroku-icon', color: '#430098', category: 'cloud' },
    
    // Security & CDN
    'cloudflare': { icon: 'logos:cloudflare', color: '#F38020', category: 'security' },
    'fastly': { icon: 'simple-icons:fastly', color: '#FF282D', category: 'cdn' },
    'akamai': { icon: 'simple-icons:akamai', color: '#0096D6', category: 'cdn' },
    'imperva': { icon: 'simple-icons:imperva', color: '#E62B2B', category: 'security' },
    'incapsula': { icon: 'simple-icons:imperva', color: '#E62B2B', category: 'security' },
    'waf': { icon: 'mdi:security', color: '#F57C00', category: 'security' },
    'firewall': { icon: 'mdi:security', color: '#F57C00', category: 'security' },
    
    // Frontend
    'react': { icon: 'logos:react', color: '#61DAFB', category: 'frontend' },
    'vue': { icon: 'logos:vue', color: '#4FC08D', category: 'frontend' },
    'angular': { icon: 'logos:angular-icon', color: '#DD0031', category: 'frontend' },
    'svelte': { icon: 'logos:svelte-icon', color: '#FF3E00', category: 'frontend' },
    'next': { icon: 'logos:nextjs-icon', color: '#000000', category: 'frontend' },
    'nextjs': { icon: 'logos:nextjs-icon', color: '#000000', category: 'frontend' },
    'nuxt': { icon: 'logos:nuxt-icon', color: '#00C58E', category: 'frontend' },
    'javascript': { icon: 'logos:javascript', color: '#F7DF1E', category: 'frontend' },
    'js': { icon: 'logos:javascript', color: '#F7DF1E', category: 'frontend' },
    'typescript': { icon: 'logos:typescript-icon', color: '#3178C6', category: 'frontend' },
    'ts': { icon: 'logos:typescript-icon', color: '#3178C6', category: 'frontend' },
    'css': { icon: 'logos:css-3', color: '#1572B6', category: 'frontend' },
    'html': { icon: 'logos:html-5', color: '#E34F26', category: 'frontend' },
    
    // Backend
    'node': { icon: 'logos:nodejs-icon', color: '#339933', category: 'backend' },
    'nodejs': { icon: 'logos:nodejs-icon', color: '#339933', category: 'backend' },
    'python': { icon: 'logos:python', color: '#3776AB', category: 'backend' },
    'django': { icon: 'logos:django-icon', color: '#092E20', category: 'backend' },
    'flask': { icon: 'logos:flask', color: '#000000', category: 'backend' },
    'java': { icon: 'logos:java', color: '#007396', category: 'backend' },
    'spring': { icon: 'logos:spring-icon', color: '#6DB33F', category: 'backend' },
    'php': { icon: 'logos:php', color: '#777BB4', category: 'backend' },
    'laravel': { icon: 'logos:laravel', color: '#FF2D20', category: 'backend' },
    'ruby': { icon: 'logos:ruby', color: '#CC342D', category: 'backend' },
    'rails': { icon: 'logos:rails', color: '#CC0000', category: 'backend' },
    'go': { icon: 'logos:go', color: '#00ADD8', category: 'backend' },
    'golang': { icon: 'logos:go', color: '#00ADD8', category: 'backend' },
    'rust': { icon: 'logos:rust', color: '#000000', category: 'backend' },
    'dotnet': { icon: 'logos:dotnet', color: '#512BD4', category: 'backend' },
    '.net': { icon: 'logos:dotnet', color: '#512BD4', category: 'backend' },
    
    // Database
    'postgres': { icon: 'logos:postgresql', color: '#336791', category: 'database' },
    'postgresql': { icon: 'logos:postgresql', color: '#336791', category: 'database' },
    'mysql': { icon: 'logos:mysql', color: '#4479A1', category: 'database' },
    'mongo': { icon: 'logos:mongodb', color: '#47A248', category: 'database' },
    'mongodb': { icon: 'logos:mongodb', color: '#47A248', category: 'database' },
    'redis': { icon: 'logos:redis', color: '#DC382D', category: 'database' },
    'sql': { icon: 'mdi:database', color: '#2196F3', category: 'database' },
    'database': { icon: 'mdi:database', color: '#2196F3', category: 'database' },
    'sqlite': { icon: 'simple-icons:sqlite', color: '#003B57', category: 'database' },
    
    // CMS
    'wordpress': { icon: 'logos:wordpress-icon', color: '#21759B', category: 'cms' },
    'wp': { icon: 'logos:wordpress-icon', color: '#21759B', category: 'cms' },
    'drupal': { icon: 'logos:drupal-icon', color: '#0678BE', category: 'cms' },
    'joomla': { icon: 'logos:joomla', color: '#5091CD', category: 'cms' },
    'shopify': { icon: 'logos:shopify', color: '#7AB55C', category: 'cms' },
    'magento': { icon: 'logos:magento', color: '#EE672F', category: 'cms' },
    
    // DevOps
    'kubernetes': { icon: 'logos:kubernetes', color: '#326CE5', category: 'devops' },
    'k8s': { icon: 'logos:kubernetes', color: '#326CE5', category: 'devops' },
    'docker': { icon: 'logos:docker-icon', color: '#2496ED', category: 'devops' },
    'terraform': { icon: 'logos:terraform-icon', color: '#7B42BC', category: 'devops' },
    'jenkins': { icon: 'logos:jenkins', color: '#D33833', category: 'devops' },
    'nginx': { icon: 'logos:nginx', color: '#009639', category: 'devops' },
    'apache': { icon: 'logos:apache', color: '#D22128', category: 'devops' },
    'ansible': { icon: 'logos:ansible', color: '#EE0000', category: 'devops' },
    
    // DevTools
    'github': { icon: 'logos:github-icon', color: '#181717', category: 'devtools' },
    'gitlab': { icon: 'logos:gitlab', color: '#FC6D26', category: 'devtools' },
    'jira': { icon: 'logos:jira', color: '#0052CC', category: 'devtools' },
    'bitbucket': { icon: 'logos:bitbucket', color: '#0052CC', category: 'devtools' },
    
    // Communication
    'slack': { icon: 'logos:slack-icon', color: '#4A154B', category: 'communication' },
    'discord': { icon: 'logos:discord-icon', color: '#5865F2', category: 'communication' },
    
    // Payment & Search
    'stripe': { icon: 'logos:stripe', color: '#008CDD', category: 'payment' },
    'paypal': { icon: 'logos:paypal', color: '#00457C', category: 'payment' },
    'analytics': { icon: 'mdi:google-analytics', color: '#F57C00', category: 'analytics' },
    'algolia': { icon: 'simple-icons:algolia', color: '#5468FF', category: 'search' },
    'cloudfront': { icon: 'logos:aws', color: '#FF9900', category: 'cdn' },
  };
  
  // 1. Direct lookup in the mapping object (exact match)
  if (techMappings[baseTechName]) {
    return techMappings[baseTechName];
  }
  
  // 2. If no exact match, try to dynamically generate an icon name based on the tech name
  
  // Clean the name for icon lookup - remove special chars, spaces, etc.
  const cleanName = baseTechName
    .replace(/[^a-z0-9]/g, '') // Remove non-alphanumeric chars
    .replace(/^[0-9]+/, '');    // Remove leading numbers
  
  // Try different icon sets in this order of preference
  const iconSets = ['logos:', 'simple-icons:', 'mdi:', 'icon-park-outline:'];
  
  // Category detection keywords
  const categoryKeywords = {
    cloud: ['cloud', 'aws', 'azure', 'gcp', 'hosting'],
    security: ['security', 'auth', 'waf', 'firewall', 'protection'],
    cdn: ['cdn', 'delivery', 'cache', 'edge'],
    frontend: ['js', 'ui', 'css', 'html', 'front', 'web'],
    backend: ['api', 'server', 'back', 'lang', 'function'],
    database: ['db', 'sql', 'data', 'store', 'cache'],
    cms: ['cms', 'content', 'manage', 'site'],
    devops: ['ops', 'deploy', 'ci', 'cd', 'container', 'kubernetes'],
    payment: ['pay', 'checkout', 'transaction', 'money', 'finance'],
    analytics: ['analytics', 'track', 'metric', 'measure', 'insight'],
    search: ['search', 'find', 'query', 'index'],
    communication: ['chat', 'message', 'communicate', 'notify']
  };
  
  // Generate a consistent color based on the tag name
  const hash = baseTechName.split('').reduce((acc, char) => char.charCodeAt(0) + ((acc * 32) - acc), 0);
  const hue = hash % 360;
  const color = `hsl(${hue}, 80%, 40%)`;
  
  // Try to guess category from name if possible
  let category = 'other';
  
  // Check each category's keywords for a match - replace for...of loop with array method
  const categoryEntry = Object.entries(categoryKeywords).find(([_, keywords]) => 
    keywords.some(keyword => baseTechName.includes(keyword))
  );
  
  if (categoryEntry) {
    category = categoryEntry[0]; // Set the category to the found category name
  }
  
  // Attempt to dynamically find an icon that might exist
  // Try different icon sets and naming patterns
  
  // If cleanName is empty after cleaning, use a generic icon
  if (!cleanName) {
    return {
      icon: 'mdi:code-tags',
      color,
      category
    };
  }
  
  // Try to find an icon in the different icon libraries
  // Return on first attempt - let Iconify handle fallbacks if icons don't exist
  return {
    // Try in order: logos:name, simple-icons:name, mdi:name-outline
    icon: `${iconSets[0]}${cleanName}`,
    color,
    category,
    // Include all possible icon options as fallbacks
    iconFallbacks: [
      `${iconSets[0]}${cleanName}`,
      `${iconSets[1]}${cleanName}`,
      `${iconSets[2]}${cleanName}`,
      `${iconSets[2]}${cleanName}-outline`,
      `${iconSets[3]}${cleanName}`,
      'mdi:code-tags' // Ultimate fallback
    ]
  };
};

export default function TagSearchView() {
  const theme = useTheme();
  const [searchQuery, setSearchQuery] = useState('');
  const [recentSearches, setRecentSearches] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [queryDuration, setQueryDuration] = useState(null);
  const [activeSearchQuery, setActiveSearchQuery] = useState('');
  const searchInputRef = useRef(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [showCopySuccess, setShowCopySuccess] = useState(false);
  const ITEMS_PER_PAGE = 20;

  const { loading, error, data, refetch } = useQuery(SEARCH_TAGS_AND_HEADERS, {
    variables: {
      searchQuery: `%${activeSearchQuery}%`,
    },
    skip: !activeSearchQuery,
  });

  useEffect(() => {
    const storedSearches = JSON.parse(localStorage.getItem('tagRecentSearches') || '[]');
    setRecentSearches(storedSearches);
  }, []);

  const updateRecentSearches = useCallback((newSearch) => {
    const maxRecentSearches = 5;
    setRecentSearches((prevSearches) => {
      const updatedSearches = [
        newSearch,
        ...prevSearches.filter((search) => search !== newSearch),
      ].slice(0, maxRecentSearches);
      localStorage.setItem('tagRecentSearches', JSON.stringify(updatedSearches));
      return updatedSearches;
    });
  }, []);

  const handleSearch = useCallback(async () => {
    if (!searchQuery.trim()) return;
    
    setQueryDuration(null);
    setActiveSearchQuery(searchQuery);
    const startTime = Date.now();
    
    try {
      setIsLoading(true);
      await refetch({
        searchQuery: `%${searchQuery}%`,
      });
      updateRecentSearches(searchQuery);
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setIsLoading(false);
      setQueryDuration((Date.now() - startTime) / 1000);
    }
  }, [searchQuery, refetch, updateRecentSearches]);

  const handleRecentSearchClick = (search) => {
    setSearchQuery(search);
    setActiveSearchQuery(search);
  };

  const handleSearchQueryChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const getIPColor = (ip, allDomains) => {
    // Check if this IP is shared by multiple domains
    const ipCount = allDomains.filter(domain => 
      domain.ip?.ip === ip
    ).length;
    const isSharedIP = ipCount > 1;

    return {
      background: isSharedIP 
        ? alpha(theme.palette.primary.main, 0.1)
        : alpha(theme.palette.grey[500], 0.1),
      color: isSharedIP
        ? theme.palette.primary.main
        : theme.palette.grey[700],
    };
  };

  const renderDomainCard = (domain) => {
    const ipAddress = domain.ip?.ip;
    const { background, color } = ipAddress 
      ? getIPColor(ipAddress, data.live_domains)
      : { background: 'transparent', color: 'text.primary' };

    // Extract tags and headers from associations
    const tags = domain.technology_tags?.map(t => t.domain_tag.tag) || [];
    const headers = domain.header_tags?.map(h => h.domain_header.header) || [];

    // Get organization and target names with null checks
    const orgName = domain.target?.organisation?.name || 'Unknown Organization';
    const targetName = domain.target?.name || 'Unknown Target';

    return (
      <Paper
        key={domain.id}
        sx={{
          p: 2,
          mb: 2,
          borderRadius: 2,
          boxShadow: theme.customShadows.card,
          height: 280,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Stack spacing={2} sx={{ height: '100%' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Link 
                href={`${domain.domain}`} 
                target="_blank"
                rel="noopener noreferrer"
                sx={{ 
                  color: 'primary.main',
                  typography: 'subtitle1',
                  fontWeight: 'bold',
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline' },
                  wordBreak: 'break-all',
                  display: 'block'
                }}
              >
                {domain.domain}
              </Link>
              <Typography 
                variant="body2" 
                color="text.secondary"
                sx={{ wordBreak: 'break-all' }}
              >
                {orgName}
              </Typography>
            </Box>
            {domain.status_code && (
              <Label
                color={
                  domain.status_code >= 200 && domain.status_code < 300
                    ? 'success'
                    : domain.status_code >= 300 && domain.status_code < 400
                    ? 'warning'
                    : 'error'
                }
                sx={{ ml: 1, flexShrink: 0 }}
              >
                {domain.status_code}
              </Label>
            )}
          </Box>

          {ipAddress && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip
                label={ipAddress}
                sx={{
                  backgroundColor: background,
                  color,
                  '& .MuiChip-label': {
                    fontWeight: 'medium',
                  },
                }}
              />
              {['cloud_provider_type', 'cdn_type', 'waf_type'].map(providerType => {
                const type = domain.ip?.[providerType];
                if (!type || type === 'N/A') return null;
                
                const { icon, color } = getProviderIcon(type, providerType);
                const label = `${providerType.split('_')[0].toUpperCase()}: ${type}`;
                
                return icon ? (
                  <Tooltip key={providerType} title={label}>
                    <IconButton size="small">
                      <Iconify 
                        icon={icon}
                        width={20}
                        sx={{ color }}
                      />
                    </IconButton>
                  </Tooltip>
                ) : (
                  <Chip
                    key={providerType}
                    label={type}
                    size="small"
                    color={getProviderTypeColor(providerType.split('_')[0])}
                    sx={{
                      height: 24,
                      fontSize: '0.75rem'
                    }}
                  />
                );
              })}
            </Box>
          )}

          <Box sx={{ 
            flex: 1,
            overflow: 'auto',
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'transparent',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: (theme) => alpha(theme.palette.grey[500], 0.2),
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              backgroundColor: (theme) => alpha(theme.palette.grey[500], 0.3),
            },
          }}>
            {tags.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                {tags.map((tag, index) => (
                  <Chip
                    key={index}
                    label={tag}
                    size="small"
                    sx={{
                      backgroundColor: alpha(theme.palette.info.main, 0.1),
                      color: theme.palette.info.main,
                      height: 24,
                      maxWidth: '100%',
                      '& .MuiChip-label': {
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        maxWidth: '100%',
                        padding: '0 8px'
                      }
                    }}
                  />
                ))}
              </Box>
            )}

            {headers.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {headers.map((header, index) => (
                  <Chip
                    key={index}
                    label={header}
                    size="small"
                    variant="outlined"
                    sx={{ 
                      borderColor: theme.palette.grey[300],
                      height: 24,
                      maxWidth: '100%', 
                      '& .MuiChip-label': {
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        maxWidth: '100%',
                        padding: '0 8px'
                      }
                    }}
                  />
                ))}
              </Box>
            )}
          </Box>
        </Stack>
      </Paper>
    );
  };

  const handleCopyAllDomains = () => {
    if (!data?.live_domains) return;
    const allDomains = [...new Set(data.live_domains.map(domain => domain.domain))].join('\n');
    navigator.clipboard.writeText(allDomains);
    setShowCopySuccess(true);
  };

  const handleCopyAllIPs = () => {
    if (!data?.live_domains) return;
    const allIPs = [...new Set(data.live_domains
      .map(domain => domain.ip?.ip)
      .filter(Boolean))].join('\n');
    navigator.clipboard.writeText(allIPs);
    setShowCopySuccess(true);
  };

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Calculate pagination
  const getPaginatedData = (data) => {
    if (!data?.live_domains) return [];
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return data.live_domains.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  };

  const handleTechIconClick = (techName) => {
    setSearchQuery(techName);
    setActiveSearchQuery(techName);
    
    // Simulate the search process
    const startTime = Date.now();
    setIsLoading(true);
    
    refetch({
      searchQuery: `%${techName}%`,
    })
      .then(() => {
        updateRecentSearches(techName);
        setQueryDuration((Date.now() - startTime) / 1000);
      })
      .catch((error) => {
        console.error('Search error:', error);
      })
      .finally(() => {
        setIsLoading(false);
      });
    
    // Scroll to results
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setActiveSearchQuery('');
    setCurrentPage(1);
  };

  return (
    <Container maxWidth="xl">
      <Typography variant="h4" sx={{ mb: 5 }}>
        Tag & Header Search
      </Typography>

      <SearchSection
        searchQuery={searchQuery}
        onSearchQueryChange={handleSearchQueryChange}
        onSearch={handleSearch}
        recentSearches={recentSearches}
        onRecentSearchClick={handleRecentSearchClick}
        searchInputRef={searchInputRef}
        isLoading={isLoading}
        queryDuration={queryDuration}
        activeSearchQuery={activeSearchQuery}
        disabled={!searchQuery.trim()}
      />

      {/* Tech Icons Display */}
      {!activeSearchQuery && <TechIconsDisplay onTechClick={handleTechIconClick} />}

      {loading && <LoadingBar />}

      {error && (
        <Typography color="error" sx={{ mt: 2 }}>
          Error: {error.message}
        </Typography>
      )}

      {data && activeSearchQuery && (
        <>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            mb: 3 
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Button
                variant="text"
                size="small"
                startIcon={<Iconify icon="mdi:arrow-left" />}
                onClick={handleClearSearch}
                sx={{ ml: 1 }}
              >
                Back to Tech Icons
              </Button>
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={handleCopyAllDomains}
                startIcon={<Iconify icon="mdi:content-copy" />}
              >
                Copy All Domains
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={handleCopyAllIPs}
                startIcon={<Iconify icon="mdi:content-copy" />}
              >
                Copy All IPs
              </Button>
            </Box>
          </Box>

          {/* Tags Summary - Single Count Display */}
          {(() => {
            // Extract all unique tags from the search results with total count
            const tags = new Set();
            data.live_domains.forEach(domain => {
              const domainTags = domain.technology_tags?.map(t => t.domain_tag.tag) || [];
              domainTags.forEach(tag => tags.add(tag));
            });
            
            const uniqueTagCount = tags.size;
            
            if (uniqueTagCount > 0) {
              return (
                <Box sx={{ mb: 3, maxWidth: '600px', display: 'flex', alignItems: 'center' }}>
                  <Box 
                    sx={{ 
                      mr: 2, 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      p: 1,
                      borderRadius: '50%',
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      color: theme.palette.primary.main
                    }}
                  >
                    <Iconify icon="mdi:tag-multiple" width={24} height={24} />
                  </Box>
                  <Typography variant="subtitle1">
                    Found <Box component="span" sx={{ color: theme.palette.primary.main, fontWeight: 'bold' }}>{uniqueTagCount}</Box> unique technologies across <Box component="span" sx={{ color: theme.palette.primary.main, fontWeight: 'bold' }}>{data.live_domains.length}</Box> domains
                  </Typography>
                </Box>
              );
            }
            return null;
          })()}

          <Box sx={{ mb: 3, display: 'flex', flexWrap: 'wrap', margin: -1.5 }}>
            {getPaginatedData(data).map((domain, index) => (
              <Box 
                key={domain.id} 
                sx={{ 
                  width: { xs: '100%', sm: '50%', md: '33.3333%' }, 
                  padding: 1.5, 
                  boxSizing: 'border-box' 
                }}
              >
                {renderDomainCard(domain)}
              </Box>
            ))}
          </Box>

          <Grid size={{ xs: 12 }}>
            {data.live_domains.some(domain => domain.ip?.dns_resolvable_domains?.length > 0) && (
              <Card sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Related DNS Records
                </Typography>
                <Stack spacing={2}>
                  {data.live_domains
                    .filter(domain => domain.ip?.dns_resolvable_domains?.length > 0)
                    .flatMap(domain => domain.ip.dns_resolvable_domains)
                    .map((record) => (
                      <Box key={record.domain}>
                        <Typography variant="subtitle2">{record.domain}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {record.target?.organisation?.name || 'Unknown Organization'}
                        </Typography>
                        <Divider sx={{ mt: 1 }} />
                      </Box>
                    ))}
                </Stack>
              </Card>
            )}
          </Grid>

          {data.live_domains.length > ITEMS_PER_PAGE && (
            <Stack alignItems="center" sx={{ mt: 4 }}>
              <Pagination
                count={Math.ceil(data.live_domains.length / ITEMS_PER_PAGE)}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                size="large"
              />
            </Stack>
          )}
        </>
      )}

      <Snackbar
        open={showCopySuccess}
        autoHideDuration={2000}
        onClose={() => setShowCopySuccess(false)}
        message="Copied to clipboard!"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Container>
  );
} 